import { createFeatureSelector, createSelector } from '@ngrx/store';
import { handleActions } from 'redux-actions';
import { StatementInfo } from './models/statement-info.model';
import { StatementMetrics } from './models/statement-metrics.model';
import {
  ActionsEnum,
  FetchStatementInfo,
  FetchStatementInfoError,
  FetchStatementInfoSuccess,
  FetchStatementMetrics,
  FetchStatementMetricsError,
  FetchStatementMetricsSuccess,
  UpdateStatementInfo,
  UpdateStatementMetrics,
} from './statement-data.actions';

// feature name
export const FEATURE_NAME = 'statement-data';

// state interface
export interface IFetchedForClient {
  [clientId: string]: boolean;
}

export interface IStatementInfoForClient {
  [clientId: string]: StatementInfo;
}

export interface IStatementMetricsForClient {
  [clientId: string]: StatementMetrics;
}

export interface IState {
  statementInfo: IStatementInfoForClient;
  statementInfoFetched: IFetchedForClient;
  fetchStatementError: Error;
  statementMetrics: IStatementMetricsForClient;
  statementMetricsFetched: IFetchedForClient;
  fetchStatementMetricsError: Error;
}

// initial state of store
export const initialState: IState = {
  statementInfo: {},
  statementInfoFetched: {},
  fetchStatementError: null,
  statementMetrics: {},
  statementMetricsFetched: {},
  fetchStatementMetricsError: null,
};

// reducers
const reducerFn = handleActions(
  {
    [ActionsEnum.FETCH_STATEMENT_INFO]: function (
      state: IState,
      { payload }: FetchStatementInfo
    ): IState {
      const { clientId } = payload;
      const statementInfoFetched = fetchedForClient(
        state.statementInfoFetched,
        clientId,
        false
      );

      return {
        ...state,
        statementInfoFetched,
        fetchStatementError: null,
      };
    },
    [ActionsEnum.FETCH_STATEMENT_INFO_SUCCESS]: function (
      state: IState,
      { payload }: FetchStatementInfoSuccess
    ): IState {
      const { clientId } = payload;
      const statementInfoFetched = fetchedForClient(
        state.statementInfoFetched,
        clientId,
        true
      );

      return {
        ...state,
        statementInfoFetched,
        fetchStatementError: null,
      };
    },
    [ActionsEnum.FETCH_STATEMENT_INFO_ERROR]: function (
      state: IState,
      { payload }: FetchStatementInfoError
    ): IState {
      const { clientId, error } = payload;
      const statementInfoFetched = fetchedForClient(
        state.statementInfoFetched,
        clientId,
        true
      );

      return {
        ...state,
        statementInfoFetched,
        fetchStatementError: error,
      };
    },
    [ActionsEnum.UPDATE_STATEMENT_INFO]: function (
      state: IState,
      { payload }: UpdateStatementInfo
    ): IState {
      const { clientId, statementInfo } = payload;

      return {
        ...state,
        statementInfo: statementInfoForClient(
          state.statementInfo,
          clientId,
          statementInfo
        ),
      };
    },
    [ActionsEnum.FETCH_STATEMENT_METRICS]: function (
      state: IState,
      { payload }: FetchStatementMetrics
    ): IState {
      const { clientId } = payload;
      const statementMetricsFetched = fetchedForClient(
        state.statementMetricsFetched,
        clientId,
        false
      );

      return {
        ...state,
        statementMetricsFetched,
      };
    },
    [ActionsEnum.FETCH_STATEMENT_METRICS_SUCCESS]: function (
      state: IState,
      { payload }: FetchStatementMetricsSuccess
    ): IState {
      const { clientId } = payload;
      const statementMetricsFetched = fetchedForClient(
        state.statementMetricsFetched,
        clientId,
        true
      );

      return {
        ...state,
        statementMetricsFetched,
        fetchStatementMetricsError: null,
      };
    },
    [ActionsEnum.FETCH_STATEMENT_METRICS_ERROR]: function (
      state: IState,
      { payload }: FetchStatementMetricsError
    ): IState {
      const { clientId, error } = payload;
      const statementMetricsFetched = fetchedForClient(
        state.statementMetricsFetched,
        clientId,
        true
      );

      return {
        ...state,
        statementMetricsFetched,
        fetchStatementMetricsError: error,
      };
    },
    [ActionsEnum.UPDATE_STATEMENT_METRICS]: function (
      state: IState,
      { payload }: UpdateStatementMetrics
    ): IState {
      const { clientId, statementMetrics } = payload;

      return {
        ...state,
        statementMetrics: statementMetricsForClient(
          state.statementMetrics,
          clientId,
          statementMetrics
        ),
      };
    },
  } as any,
  initialState
);

// required for AOT
export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

// selectors
const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export function getStatementInfo(clientId: string) {
  return createSelector(
    selectFeature,
    (state: IState) => state.statementInfo[clientId]
  );
}

export function getStatementInfoFetched(clientId: string) {
  return createSelector(
    selectFeature,
    (state: IState) => state.statementInfoFetched[clientId]
  );
}

export function getStatementMetrics(clientId: string) {
  return createSelector(
    selectFeature,
    (state: IState) => state.statementMetrics[clientId]
  );
}

export function getStatementMetricsFetched(clientId: string) {
  return createSelector(
    selectFeature,
    (state: IState) => state.statementMetricsFetched[clientId]
  );
}

// helpers
function fetchedForClient(
  state: IFetchedForClient,
  clientId: string,
  fetched: boolean
): IFetchedForClient {
  return {
    ...state,
    [clientId]: fetched,
  };
}

function statementInfoForClient(
  state: IStatementInfoForClient,
  clientId: string,
  statementInfo: StatementInfo
): IStatementInfoForClient {
  return {
    ...state,
    [clientId]: statementInfo,
  };
}

function statementMetricsForClient(
  state: IStatementMetricsForClient,
  clientId: string,
  statementMetrics: StatementMetrics
): IStatementMetricsForClient {
  return {
    ...state,
    [clientId]: statementMetrics,
  };
}
